/* Used global Wordpress php variables to the ajax requests */
/* global themeVars */
/* global grecaptcha */

/**
 * A class representing the Optery Signup Forms.
 *
 * @class
 */
class OpterySignUpForm {

	/**
	 * Initializes the object with the forms and sets up event listeners.
	 *
	 * @class
	 */
	constructor() {

		this.signUpForm = document.getElementById( 'optery-signup-form' );

		if ( this.signUpForm ) {

			this.setupInputListeners( this.signUpForm );
			this.setupRecaptcha();

		}

	}

	/**
	 * Removes error messages when the user types in input fields.
	 *
	 * @param {HTMLFormElement} form - The form element.
	 */
	setupInputListeners( form ) {

		const textInputs = form.querySelectorAll( 'input[type="text"], input[type="email"], input[type="password"]' );

		textInputs.forEach( input => {

			input.addEventListener( 'keyup', () => {

				this.clearErrorMessages( form );

			} );

		} );

	}

	/**
	 * Removes error messages within the specified form.
	 *
	 * @param {HTMLFormElement} form - The form element.
	 */
	clearErrorMessages( form ) {

		const errorMessages = form.querySelectorAll( '.error-msg' );
		errorMessages.forEach( msg => msg.remove() );

	}

	/**
	 * Validates the form fields.
	 *
	 * @param {HTMLFormElement} form - The form element.
	 * @return {boolean} True if all fields are valid, false otherwise.
	 */
	validateForm( form ) {

		let fieldsValidated = true;

		const firstNameInput = form.querySelector( '[name="first_name"]' );
		const lastNameInput = form.querySelector( '[name="last_name"]' );
		const emailInput = form.querySelector( '[name="email"]' );
		const passwordInput = form.querySelector( '[name="password"]' );

		const firstName = firstNameInput.value.trim();
		if ( firstName.length < 2 ) {

			this.showError( firstNameInput, 'First name must contain at least 2 characters.' );
			fieldsValidated = false;

		}

		const lastName = lastNameInput.value.trim();
		if ( lastName.length < 2 ) {

			this.showError( lastNameInput, 'Last name must contain at least 2 characters.' );
			fieldsValidated = false;

		}

		const email = emailInput.value.trim();
		if ( ! this.validateEmail( email ) ) {

			this.showError( emailInput, 'Invalid Email.' );
			fieldsValidated = false;

		}

		const password = passwordInput.value;
		if ( ! this.validatePassword( password ) ) {

			this.showError( passwordInput, 'Password must be 8 characters long and include one lowercase and one uppercase letter.' );
			fieldsValidated = false;

		}

		return fieldsValidated;

	}

	/**
	 * Executes reCAPTCHA and returns the token.
	 *
	 * @return {Promise<string|null>} The reCAPTCHA token or null if failed.
	 */
	async executeRecaptcha() {

		try {

			return new Promise( ( resolve, reject ) => {

				grecaptcha.ready( () => {


					grecaptcha.execute()
						.then( token => {

							resolve( token );

						} )
						.catch( error => {

							reject( error );

						} );

				} );

			} );

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'reCAPTCHA error:', error );
			return null;

		}

	}

	/**
	 * Sets up the event listener for the sign-up form submission.
	 */
	setupSignUpForm() {

		this.signUpForm.addEventListener( 'submit', ( event ) => {

			event.preventDefault();
			grecaptcha.execute();

		} );

	}

	/**
	 * Handles the form submission.
	 *
	 */
	async handleFormSubmit() {

		// Remove existing error messages
		this.clearErrorMessages( this.signUpForm );

		// Validate form
		if ( !this.validateForm( this.signUpForm ) ) {

			return;

		}

		// Get the reCAPTCHA token from the hidden input
		const recaptchaToken = this.signUpForm.querySelector( ".g-recaptcha-hidden" ).value;

		// Submit form
		this.submitForm( recaptchaToken );

	}

	/**
	 * Submits the form data to the server.
	 *
	 * @param {string} recaptchaToken - The reCAPTCHA token.
	 */
	submitForm( recaptchaToken ) {

		// Prepare form data
		const formData = new FormData( this.signUpForm );

		// Add reCAPTCHA token to form data
		formData.append( 'recaptcha_token', recaptchaToken );

		// Convert form data to JSON
		const data = {};
		formData.forEach( ( value, key ) => {

			if ( key !== 'g-recaptcha-response' ) {

				data[key] = value;

			}

		} );

		// Send AJAX request
		fetch( themeVars.signup_api, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify( data )
		} )
			.then( response => response.json() )
			.then( responseData => {

				if ( responseData.success ) {

					window.location.href = responseData.url;

				} else {

					// Handle server-side validation errors
					this.showFormMessage( this.signUpForm, responseData.error || 'An error occurred.', 'error' );

				}

			} )
			.catch( error => {

				// eslint-disable-next-line no-console
				console.error( 'Error:', error );
				this.showFormMessage( this.signUpForm, 'An error occurred. Please try again.', 'error' );

			} );

	}

	/**
	 * Displays an error message after the specified input element.
	 *
	 * @param {HTMLElement} element - The element after which to display the error.
	 * @param {string} message - The error message to display.
	 */
	showError( element, message ) {

		const errorSpan = document.createElement( 'span' );
		errorSpan.classList.add( 'error-msg' );
		errorSpan.textContent = message;

		element.parentNode.insertBefore( errorSpan, element.nextSibling );

	}

	/**
	 * Validates an email address.
	 *
	 * @param {string} email - The email address to validate.
	 * @return {boolean} True if valid, false otherwise.
	 */
	validateEmail( email ) {

		const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return pattern.test( email );

	}

	/**
	 * Validates a phone number.
	 *
	 * @param {string} phone - The phone number to validate.
	 * @return {boolean} True if valid, false otherwise.
	 */
	validatePhone( phone ) {

		const pattern = /^(\d{10}|\(\d{3}\)\s?\d{3}-\d{4})$/;
		return pattern.test( phone );

	}

	/**
	 * Validates the password based on specified rules.
	 *
	 * @param {string} password - The password to validate.
	 * @return {boolean} True if valid, false otherwise.
	 */
	validatePassword( password ) {

		if ( password.length < 8 ) {

			return false;

		}

		const rules = [
			{
				pattern: /[A-Z]/,
				message: 'Must contain at least one uppercase letter.'
			},
			{
				pattern: /[a-z]/,
				message: 'Must contain at least one lowercase letter.'
			}
			// Uncomment and adjust as needed
			// {
			//     pattern: /[0-9]/,
			//     message: 'Must contain at least one number.'
			// },
			// {
			//     pattern: /[!@#\$%\^&\*]/,
			//     message: 'Must contain at least one special character.'
			// }
		];

		for ( const rule of rules ) {

			if ( ! rule.pattern.test( password ) ) {

				return false;

			}

		}

		return true;

	}

	/**
	 * Displays a message in the form messages area.
	 *
	 * @param {HTMLFormElement} form - The form element.
	 * @param {string} message - The message to display.
	 * @param {string} type - The type of message ('success' or 'error').
	 */
	showFormMessage( form, message, type ) {

		//Remove exist error
		const existingMessages = form.querySelectorAll( '.form-message' );
		existingMessages.forEach( msg => msg.remove() );

		const messageElement = document.createElement( 'div' );
		messageElement.classList.add( 'form-message', type );
		messageElement.textContent = message;
		form.appendChild( messageElement );

	}

	/**
	 * Sets up the reCAPTCHA functionality.
	 */
	setupRecaptcha() {

		/**
		 * Handle reCAPTCHA token
		 * @param {string} token
		 */
		window.onSubmit = ( token ) => {

			document.querySelector( ".g-recaptcha-hidden" ).value = token;
			this.handleFormSubmit();

		};

		const formSbtElement = this.signUpForm.querySelector( "input[type='submit']" );
		if ( formSbtElement ) {

			formSbtElement.addEventListener( "click", ( event ) => {

				event.preventDefault();
				grecaptcha.execute();

			} );

		}

	}

}

// Initialize the form handling when the DOM is fully loaded
document.addEventListener( 'DOMContentLoaded', () => {

	new OpterySignUpForm();

} );
